import { useCallback, useEffect, useState } from 'react';
import { GameAction, GameError, GameState } from '../game';
import { dispatchGameAction, subscribeToGame } from '../services/gameRepository';

export function useGame(gameId: string | undefined) {
  const [game, setGame] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!gameId) {
      setGame(null);
      setLoading(false);
      setError('Missing game code');
      return undefined;
    }

    setLoading(true);
    setError('');
    const unsubscribe = subscribeToGame(
      gameId,
      (next) => {
        setGame(next);
        setLoading(false);
        if (!next) {
          setError('Game not found. Check the code and try again.');
        }
      },
      (subscribeError) => {
        setLoading(false);
        setError(humanizeFirebaseError(subscribeError));
      }
    );

    return unsubscribe;
  }, [gameId]);

  const dispatch = useCallback(
    async (action: GameAction) => {
      if (!gameId) {
        throw new GameError('Missing game code');
      }
      try {
        setError('');
        return await dispatchGameAction(gameId, action);
      } catch (err) {
        const message = humanizeFirebaseError(err);
        setError(message);
        throw err;
      }
    },
    [gameId]
  );

  const clearError = useCallback(() => setError(''), []);

  return { game, loading, error, dispatch, setError, clearError };
}

export function humanizeFirebaseError(err: unknown): string {
  if (err instanceof GameError) {
    return err.message;
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code?: string }).code);
    if (code === 'permission-denied') {
      return 'You do not have access to this game. Sign in and confirm you joined with the right code.';
    }
    if (code === 'unavailable') {
      return 'Could not reach Firestore. Check your connection (and that the emulator is running, if you are developing locally).';
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Something went wrong';
}
